import { ReactNode } from 'react';
import { useLocation } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import { Link } from '@/components/Link/Link';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tabs from '@/design_system/Tabs';
import { Tab, TabList } from '@/design_system/Tabs/Tabs';
import Title from '@/design_system/Typography/Title';
import IconChevron from '@/icons/Chevron.svg';
import { createBEMClasses } from '@/utils/classname';
import useViewport from '@/utils/useViewport';

import './Layout.css';

const { block, element } = createBEMClasses('settings-section');

export const SettingsWIP = () => (
  <main>
    <Trans id="settings.wip">WIP 🚧</Trans>
  </main>
);

export type SettingsSectionTab = {
  id: string;
  label: ReactNode;
};

/**
 * Settings beta page shell for a section: an H1 and path-based tabs on list pages,
 * nothing but the content on record pages (`/settings_beta/<section>/<tab>/<record>`).
 */
export const SettingsSectionLayout = ({
  title,
  tabs = [],
  basePath,
  children,
}: {
  title: ReactNode;
  tabs?: SettingsSectionTab[];
  /** Path the tabs are relative to. Defaults to `/settings_beta/<section>`. */
  basePath?: string;
  children: ReactNode;
}) => {
  const { t } = useLingui();
  const { pathname } = useLocation();
  const { isMobile } = useViewport();

  const sectionPath = basePath ?? `/settings_beta/${pathname.split('/')[2] ?? ''}`;
  const [tabId = '', ...recordPath] = pathname
    .slice(sectionPath.length)
    .split('/')
    .filter((segment) => !!segment);
  const isRecord = recordPath.length > 0;

  if (isRecord) {
    return <div className={block({ record: true }, 'container')}>{children}</div>;
  }

  return (
    <>
      <div className="header">
        <Stack row alignItems="center" gap="1rem">
          {isMobile && (
            <Button
              iconOnly
              variant="secondary"
              to="/settings_beta"
              ariaLabel={t({
                id: 'settings-section.back-to-settings',
                message: 'Back to Settings',
              })}
            >
              <IconChevron left />
            </Button>
          )}
          <Title size="200" as="h1">
            {title}
          </Title>
        </Stack>
      </div>
      <div className={block(undefined, 'container container--list')}>
        {tabs.length > 0 && (
          <Tabs selectedKey={tabId} className={element('tabs')}>
            <TabList>
              {tabs.map((tab) => (
                <Tab key={tab.id} id={tab.id} href={`${sectionPath}/${tab.id}`}>
                  {tab.label}
                </Tab>
              ))}
            </TabList>
          </Tabs>
        )}
        {children}
      </div>
    </>
  );
};

/**
 * Leading part of a record breadcrumb (`Parent >`), placed before the record title.
 * On mobile, it is a back button to the parent list, like the one on list pages.
 */
export const SettingsBreadcrumbParent = ({ label, to }: { label: ReactNode; to: string }) => {
  const { isMobile } = useViewport();

  if (isMobile) {
    return (
      <Button iconOnly variant="secondary" to={to} className={element('back')}>
        <IconChevron left />
        <span className="visually-hidden">{label}</span>
      </Button>
    );
  }

  return (
    <span className={element('breadcrumb')}>
      <Link to={to} className="reset-link">
        {label}
      </Link>
      <span aria-hidden="true">&gt;</span>
    </span>
  );
};
